import React from 'react';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Globe, Heart, Zap, Users, Award, Linkedin, Twitter, Mail } from 'lucide-react';

const AboutUs = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const founders = [
    {
      name: "Suganthi Ravichandran",
      role: "Founder",
      title: "Business & Operations",
      image: "https://customer-assets.emergentagent.com/job_d00c4bc8-9da3-4ce7-be0b-a2395f7d38eb/artifacts/95a0yrpx_Suganthi.png",
      bio: "Suganthi Ravichandran is the co-founder of Deyvainfotech and the driving force behind the business operations, strategy, and growth of Typeit 4U. With a strong background in the IT industry, she brings structure, direction, and execution to the company.",
      highlight: "Alongside Ravichandran, Suganthi made the leap from a stable IT career to entrepreneurship — driven by the belief that Indian users deserved better digital tools built specifically for them.",
      responsibilities: "Founder, Business Operations, Growth & Strategy",
      color: "pink"
    },
    {
      name: "Ravichandran Karunakaran",
      role: "Co-Founder & MD",
      title: "UI & UX / Android Dev | Vibe Coder",
      image: "https://customer-assets.emergentagent.com/job_d00c4bc8-9da3-4ce7-be0b-a2395f7d38eb/artifacts/dktt0vv4_Ravi.png",
      bio: "Ravichandran is the technical brain behind Typeit 4U. An experienced Android developer with a strong background in the Indian IT industry, he conceptualised, designed, and built the Typeit 4U keyboard entirely from the ground up.",
      highlight: "After years in corporate IT, Ravichandran saw a gap that no one was filling — a keyboard that truly understood Indian users, Indian languages, and Indian communication needs.",
      responsibilities: "Managing Director, Lead Android Developer, Product Vision",
      color: "orange"
    }
  ];

  const values = [
    {
      icon: Globe,
      title: "India First",
      emoji: "🇮🇳",
      description: "Every feature is designed around real Indian use cases, languages, and communication habits."
    },
    {
      icon: Heart,
      title: "Privacy First",
      emoji: "🔒",
      description: "We never read your messages or keyboard input. You are always in control of what AI sees."
    },
    {
      icon: Zap,
      title: "Builder Mindset",
      emoji: "🛠️",
      description: "We build, test, and improve constantly. Typeit 4U today is just the beginning."
    },
    {
      icon: Users,
      title: "User Respect",
      emoji: "🤝",
      description: "No dark patterns. No fake numbers. No misleading features. Only honest products."
    },
    {
      icon: Award,
      title: "Accessibility",
      emoji: "🆓",
      description: "Core features are free. We believe great AI tools should be accessible to every Indian."
    }
  ];

  const stats = [
    { number: "50,000+", label: "Active Users" },
    { number: "4.6/5", label: "App Rating" },
    { number: "15+", label: "AI Features" },
    { number: "22", label: "Indian Languages" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 via-white to-pink-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-200/30 to-pink-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-pink-200/30 to-orange-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200 text-orange-700 px-5 py-2.5 rounded-full mb-8 shadow-sm">
              <span className="text-lg">🇮🇳</span>
              <span className="text-sm font-semibold">Made in India, Built for India</span>
            </div>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ color: '#0A0A0A' }}
            >
              We Build for the Billions Who Type
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Typeit 4U was born from a simple frustration — existing keyboards were not built for how Indians actually communicate. We changed that.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
                className="text-center"
              >
                <div 
                  className="text-3xl md:text-4xl font-bold mb-1"
                  style={{
                    background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Founders - Professional Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Meet the Founders
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
              Two IT professionals who chose to build something of their own — for India.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {founders.map((founder, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.2 }}
                className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow border border-gray-100"
              >
                {/* Image Section */}
                <div className={`relative h-80 bg-gradient-to-br ${founder.color === 'pink' ? 'from-pink-100 to-orange-50' : 'from-orange-100 to-pink-50'}`}>
                  <img 
                    src={founder.image} 
                    alt={founder.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${founder.color === 'pink' ? 'bg-pink-500' : 'bg-orange-500'}`}>
                      {founder.role}
                    </div>
                    <h3 className="text-2xl font-bold">{founder.name}</h3>
                    <p className="text-white/80 text-sm">{founder.title}</p>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {founder.bio}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6 italic border-l-4 border-orange-300 pl-4">
                    {founder.highlight}
                  </p>
                  
                  <div className={`rounded-xl p-4 ${founder.color === 'pink' ? 'bg-pink-50' : 'bg-orange-50'}`}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Responsibilities</p>
                    <p className="text-sm text-gray-800 font-medium">{founder.responsibilities}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Our Story
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8 italic">
              From a corporate IT job to building India's AI keyboard — this is how Typeit 4U came to life.
            </p>
            
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>Ravichandran Karunakaran</strong> and <strong>Suganthi Ravichandran</strong> spent years working in the Indian IT industry. Like millions of Indian professionals, they typed in Tamil at home, Hindi with colleagues, and English at work — switching between apps, copy-pasting translations, and struggling to find words that felt natural in a second language.
              </p>
              <p>
                Every day, they saw the same problem: <strong>keyboards were built for the West.</strong> They were fast, yes. But they didn't understand India. They didn't understand that an Indian user might type 'naan yaruku solrathu' and need it in professional English before hitting send.
              </p>
              <p>
                So Ravichandran and Suganthi made a decision that changed everything — they <strong>left their IT jobs and started Deyvainfotech</strong> with one goal: build technology that truly works for India.
              </p>
            </div>

            <div className="mt-10 bg-gradient-to-r from-orange-50 to-pink-50 border-l-4 border-orange-500 p-6 rounded-r-2xl">
              <p className="text-lg italic text-gray-800 mb-2">
                "We didn't want to build another keyboard. We wanted to build <strong>the keyboard that India deserved.</strong>"
              </p>
              <p className="text-sm text-gray-600">
                — Ravichandran Karunakaran, MD & Co-founder, Deyvainfotech
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-orange-100">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
                Our Mission
              </h3>
              <p className="text-gray-700 leading-relaxed">
                "To build AI-powered tools that make everyday digital communication easier, faster, and more natural for every Indian — in their language, in their style, on their terms."
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-pink-100">
              <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
                Our Vision
              </h3>
              <p className="text-gray-700 leading-relaxed">
                "A future where every Indian can communicate confidently in any language, on any platform, without friction — powered by AI they can trust."
              </p>
            </div>
          </motion.div>

          {/* Values */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h3 className="text-3xl font-bold mb-10 text-center" style={{ color: '#0A0A0A' }}>
              Our Values
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-3xl mb-3">{value.emoji}</div>
                  <h4 className="text-lg font-semibold mb-2" style={{ color: '#0A0A0A' }}>
                    {value.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-24 bg-white">
        <div className="container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-3xl p-8 md:p-12 border border-orange-100"
          >
            <h2 className="text-3xl font-bold mb-4 text-center" style={{ color: '#0A0A0A' }}>
              About Deyvainfotech
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8 text-center">
              The company behind Typeit 4U
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-2xl mx-auto">
              <strong>Deyvainfotech</strong> is a Chennai-based Indian technology company founded by Ravichandran Karunakaran and Suganthi Ravichandran. The company builds digital products focused on making technology more accessible and useful for Indian users.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Company", value: "Deyvainfotech" },
                { label: "Product", value: "Typeit 4U" },
                { label: "Location", value: "Chennai, India" },
                { label: "Platform", value: "Android" },
                { label: "Languages", value: "22 Indian" },
                { label: "Founded", value: "India 🇮🇳" },
                { label: "Email", value: "support@deyvainfotech.com" },
                { label: "Website", value: "typeit4u.com" }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-900 break-words">{item.value}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
              Ready to Try What We Built?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Download Typeit 4U free and experience the keyboard built for India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://play.google.com/store/apps/details?id=com.typeit4u.keyboard"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 rounded-full px-8 py-4 font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Download Free on Play Store
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-full px-8 py-4 font-medium hover:bg-white/20 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
